'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Sub-pixel scroll positions never land exactly on 0 or on the maximum. */
const EPSILON = 4;

/** Overflow state and paging of a horizontal thumbnail strip. */
export function useThumbStrip(activeIndex: number) {
    const stripRef = useRef<HTMLDivElement | null>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return;

        const update = () => {
            setCanScrollPrev(strip.scrollLeft > EPSILON);
            setCanScrollNext(strip.scrollLeft + strip.clientWidth < strip.scrollWidth - EPSILON);
        };

        update();
        strip.addEventListener('scroll', update, { passive: true });

        const observer = new ResizeObserver(update);
        observer.observe(strip);

        return () => {
            strip.removeEventListener('scroll', update);
            observer.disconnect();
        };
    }, []);

    // The active thumbnail scrolls into view, whichever control moved it — within
    // the strip only: scrollIntoView would move the page as well.
    useEffect(() => {
        const strip = stripRef.current;
        const thumb = strip?.children[activeIndex] as HTMLElement | undefined;
        if (!strip || !thumb) return;

        const stripBox = strip.getBoundingClientRect();
        const thumbBox = thumb.getBoundingClientRect();
        const offset =
            thumbBox.left < stripBox.left
                ? thumbBox.left - stripBox.left
                : Math.max(0, thumbBox.right - stripBox.right);

        if (offset) strip.scrollBy({ left: offset, behavior: 'smooth' });
    }, [activeIndex]);

    const scrollBy = useCallback((direction: -1 | 1) => {
        const strip = stripRef.current;
        if (strip) strip.scrollBy({ left: direction * strip.clientWidth * 0.8, behavior: 'smooth' });
    }, []);

    return { stripRef, canScrollPrev, canScrollNext, scrollBy };
}
